import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import AccountShell from './AccountShell';
import billingApi, { assertSafeRedirectUrl, Entitlement, Invoice } from '../../services/billingApi';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../config/firebaseConfig';
import { signInWithCustomToken } from 'firebase/auth/web-extension';
import './BillingPage.css';

// ── Helpers ───────────────────────────────────────────────────────────────

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

const getTrialDaysLeft = (expiresAt: string | null): number | null => {
  if (!expiresAt) return null;
  const msLeft = new Date(expiresAt).getTime() - Date.now();
  if (msLeft <= 0) return 0;
  return Math.ceil(msLeft / (24 * 60 * 60 * 1000));
};

const formatDate = (iso: string | null, opts?: Intl.DateTimeFormatOptions): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, opts ?? { month: 'long', day: 'numeric', year: 'numeric' });
};

const formatAmount = (cents: number, currency: string): string =>
  (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency.toUpperCase() });

const isSafeHttpsUrl = (url: string | null): url is string => {
  if (!url) return false;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

// Known status keys map to account:billing.statusLabels.<status>; an unknown
// status falls back to the raw value.
const KNOWN_STATUSES = new Set([
  'active', 'trialing', 'past_due', 'unpaid', 'canceled', 'incomplete', 'none',
]);

// ── Icons ─────────────────────────────────────────────────────────────────

const StarIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M9 2l1.8 4h4.2l-3.4 2.6 1.3 4.2L9 10.4l-3.9 2.4 1.3-4.2L3 6h4.2L9 2z" fill="currentColor" />
  </svg>
);

const FreeIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M6 9h6M9 6v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const AlertIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 2L14.5 13H1.5L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M8 6.5v3M8 11.5v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M7 4.5V7l2 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ExternalIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M5 2H2a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M7.5 1H11v3.5M11 1L5.5 6.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ErrorIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <circle cx="7.5" cy="7.5" r="6" stroke="currentColor" strokeWidth="1.3" />
    <path d="M7.5 4.5v3.5M7.5 10v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

// ── Sub-components ────────────────────────────────────────────────────────

const SubscriptionWarning: React.FC<{ entitlement: Entitlement }> = ({ entitlement }) => {
  const { t } = useTranslation(['account']);

  if (entitlement.status === 'past_due' || entitlement.status === 'unpaid') {
    return (
      <div className="bp-warning bp-warning--danger" role="alert">
        <span className="bp-warning-icon"><AlertIcon /></span>
        <div className="bp-warning-body">
          <p className="bp-warning-title">{t('account:billing.warnings.paymentFailedTitle')}</p>
          <p className="bp-warning-desc">
            {t('account:billing.warnings.paymentFailedDesc')}
          </p>
        </div>
      </div>
    );
  }

  if (entitlement.status === 'trialing' && entitlement.expiresAt) {
    const daysLeft = getTrialDaysLeft(entitlement.expiresAt);
    const endDate = formatDate(entitlement.expiresAt, { month: 'long', day: 'numeric' });
    if (daysLeft !== null && new Date(entitlement.expiresAt).getTime() - Date.now() <= THREE_DAYS_MS) {
      return (
        <div className="bp-warning bp-warning--caution" role="alert">
          <span className="bp-warning-icon"><AlertIcon /></span>
          <div className="bp-warning-body">
            <p className="bp-warning-title">{t('account:billing.warnings.trialEndingTitle')}</p>
            <p className="bp-warning-desc">
              {daysLeft === 0
                ? t('account:billing.warnings.trialEndingToday', { date: endDate })
                : t('account:billing.warnings.trialEndingInDays', { count: daysLeft, date: endDate })}
            </p>
          </div>
        </div>
      );
    }
  }

  return null;
};

const StatusIcon: React.FC<{ entitlement: Entitlement }> = ({ entitlement }) => {
  if (entitlement.status === 'past_due' || entitlement.status === 'unpaid') {
    return (
      <div className="bp-status-icon bp-status-icon--danger"><AlertIcon /></div>
    );
  }
  if (!entitlement.isPro) {
    return (
      <div className="bp-status-icon bp-status-icon--free"><FreeIcon /></div>
    );
  }
  return (
    <div className="bp-status-icon bp-status-icon--active"><StarIcon /></div>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation(['account']);
  return (
    <span className={`bp-status-pill bp-status-pill--${status}`}>
      <span className="bp-status-pill-dot" aria-hidden="true" />
      {KNOWN_STATUSES.has(status) ? t(`account:billing.statusLabels.${status}`) : status}
    </span>
  );
};

const LoadingSkeleton: React.FC = () => {
  const { t } = useTranslation(['account']);
  return (
  <div className="bp-skeleton" aria-label={t('account:billing.loading_aria')} aria-busy="true">
    <div className="bp-skeleton-row bp-skeleton-row--wide" />
    <div className="bp-skeleton-row bp-skeleton-row--mid" />
    <div className="bp-skeleton-row bp-skeleton-row--short" />
    <div className="bp-skeleton-row bp-skeleton-row--mid" />
  </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────

const BillingPage: React.FC = () => {
  const { t } = useTranslation(['account']);
  const { deleteAccount } = useAuth();
  const [entitlement, setEntitlement] = React.useState<Entitlement | null>(null);
  const [invoices, setInvoices] = React.useState<Invoice[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = React.useState(false);
  const [canceling, setCanceling] = React.useState(false);

  // Delete account state
  const [showDeleteZone, setShowDeleteZone] = React.useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState('');
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      // Always consume the custom token (ct) the extension hands off BEFORE loading
      // any billing data. The extension page lives on a different origin
      // (chrome-extension://) and can't share its Firebase session. Critically, we
      // sign in with the token even when a session already exists on account.rhemn.com:
      // a stale or switched-account session would otherwise make us load/mutate the
      // WRONG account's subscription. Awaiting it here also avoids the data load racing
      // an unresolved/stale auth state.
      const params = new URLSearchParams(window.location.search);
      const ct = params.get('ct');
      if (ct) {
        try {
          await signInWithCustomToken(auth, ct);
        } catch {
          // Fall through — getEntitlement() below surfaces a user-facing error.
        }
        // Strip the token from the URL so it isn't bookmarked or shared.
        params.delete('ct');
        const clean = params.toString() ? `?${params.toString()}` : window.location.pathname;
        window.history.replaceState(null, '', clean);
      }

      try {
        const [ent, invs] = await Promise.all([
          billingApi.getEntitlement(),
          billingApi.getInvoices().catch(() => [] as Invoice[]),
        ]);
        if (!mounted) return;
        setEntitlement(ent);
        setInvoices(invs);
        setError(null);
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : t('account:billing.errors.loadFailed'));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // `t` is referentially stable; this effect must run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCancelSubscription = async () => {
    try {
      setCanceling(true);
      setError(null);
      const result = await billingApi.cancelSubscription();
      setEntitlement((prev) =>
        prev ? { ...prev, cancelAtPeriodEnd: result.cancelAtPeriodEnd } : prev,
      );
      setConfirmCancel(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('account:billing.errors.cancelFailed'));
    } finally {
      setCanceling(false);
    }
  };

  const onDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    try {
      setDeleting(true);
      setDeleteError(null);
      // AuthContext owns the whole contract: backend deletion, local-data purge
      // (extension storage + localStorage), and session teardown on every
      // surface. On success user → null re-renders AppContent to SignInScreen
      // without any navigation; it throws only when the backend deletion itself
      // failed, with the session left intact for a retry.
      await deleteAccount();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : t('account:billing.errors.deleteFailed'));
      setDeleting(false);
    }
  };

  const onManageBilling = async () => {
    try {
      setError(null);
      const portal = await billingApi.createBillingPortalSession({
        returnUrl: `${window.location.origin}/billing`,
      });
      assertSafeRedirectUrl(portal.url, 'Billing portal');
      window.location.href = portal.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : t('account:billing.errors.portalFailed'));
    }
  };

  const trialDaysLeft =
    entitlement?.status === 'trialing' ? getTrialDaysLeft(entitlement.expiresAt ?? null) : null;

  const planLabel = entitlement?.isPro
    ? t('account:billing.planPro')
    : t('account:billing.planFree');

  const statusSublabel = (() => {
    if (!entitlement) return null;
    if (entitlement.status === 'trialing' && trialDaysLeft !== null) {
      return trialDaysLeft === 0
        ? t('account:billing.sublabel.trialEndsToday')
        : t('account:billing.sublabel.trialDaysRemaining', { count: trialDaysLeft });
    }
    if (entitlement.status === 'active' && entitlement.expiresAt) {
      return t('account:billing.sublabel.renews', {
        date: formatDate(entitlement.expiresAt, { month: 'long', day: 'numeric', year: 'numeric' }),
      });
    }
    if (entitlement.cancelAtPeriodEnd && entitlement.expiresAt) {
      return t('account:billing.sublabel.accessUntil', {
        date: formatDate(entitlement.expiresAt, { month: 'long', day: 'numeric', year: 'numeric' }),
      });
    }
    return null;
  })();

  return (
    <AccountShell section="billing">
      <div className="bp-root">

        {/* Page header */}
        <header className="bp-header">
          <h1 className="bp-title">{t('account:billing.title')}</h1>
          <p className="bp-subtitle">{t('account:billing.subtitle')}</p>
        </header>

        {/* Subscription status card */}
        <section className="bp-card" aria-label={t('account:billing.subscription_aria')}>
          <div className="bp-card-header">
            <span className="bp-card-title">{t('account:billing.subscriptionTitle')}</span>
          </div>

          <div className="bp-card-body">
            {loading && <LoadingSkeleton />}

            {!loading && entitlement && (
              <>
                {/* Warning banners */}
                <SubscriptionWarning entitlement={entitlement} />

                {/* Scheduled cancellation notice */}
                {entitlement.cancelAtPeriodEnd && entitlement.expiresAt && (
                  <div className="bp-cancel-notice" role="status">
                    <ClockIcon />
                    <span>
                      <Trans
                        i18nKey="account:billing.cancellationScheduled"
                        values={{ date: formatDate(entitlement.expiresAt) }}
                        components={{ strong: <strong /> }}
                      />
                    </span>
                  </div>
                )}

                {/* Status banner */}
                <div className="bp-status-banner">
                  <StatusIcon entitlement={entitlement} />
                  <div className="bp-status-text">
                    <span className="bp-status-label">{planLabel}</span>
                    {statusSublabel && (
                      <span className="bp-status-sublabel">{statusSublabel}</span>
                    )}
                  </div>
                </div>

                {/* Key-value details */}
                <div className="bp-kv-list">
                  <div className="bp-kv">
                    <span className="bp-kv-key">{t('account:billing.kv.status')}</span>
                    <span className="bp-kv-val">
                      <StatusPill status={entitlement.status} />
                    </span>
                  </div>

                  {entitlement.status === 'active' && entitlement.expiresAt && (
                    <div className="bp-kv">
                      <span className="bp-kv-key">{t('account:billing.kv.nextRenewal')}</span>
                      <span className="bp-kv-val">{formatDate(entitlement.expiresAt)}</span>
                    </div>
                  )}

                  {entitlement.status === 'trialing' && entitlement.expiresAt && (
                    <div className="bp-kv">
                      <span className="bp-kv-key">{t('account:billing.kv.trialEnds')}</span>
                      <span className="bp-kv-val">
                        {formatDate(entitlement.expiresAt)}
                        {trialDaysLeft !== null && trialDaysLeft > 0 && (
                          <> &middot; {t('account:billing.kv.trialDaysLeft', { count: trialDaysLeft })}</>
                        )}
                        {trialDaysLeft === 0 && <> &middot; {t('account:billing.kv.endsToday')}</>}
                      </span>
                    </div>
                  )}

                  {entitlement.plan && (
                    <div className="bp-kv">
                      <span className="bp-kv-key">{t('account:billing.kv.plan')}</span>
                      <span className="bp-kv-val">
                        {entitlement.plan === 'pro_yearly'
                          ? t('account:billing.planValues.proYearly')
                          : entitlement.plan === 'pro_monthly'
                            ? t('account:billing.planValues.proMonthly')
                            : entitlement.plan}
                      </span>
                    </div>
                  )}
                </div>

                {/* Free plan upgrade nudge */}
                {!entitlement.isPro && entitlement.status !== 'trialing' && (
                  <div className="bp-upgrade-nudge">
                    <div className="bp-upgrade-nudge-text">
                      <strong>{t('account:billing.upgradeNudge.title')}</strong>
                      {t('account:billing.upgradeNudge.desc')}
                    </div>
                    <a className="bp-upgrade-link" href="/upgrade">
                      {t('account:billing.upgradeNudge.cta')}
                    </a>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="bp-error" role="alert">
                    <ErrorIcon />
                    {error}
                  </div>
                )}

                {/* Actions */}
                <div className="bp-actions">
                  <button type="button" className="bp-primary-btn" onClick={onManageBilling}>
                    <ExternalIcon />
                    {t('account:billing.actions.manageBilling')}
                  </button>

                  {/* Cancel flow */}
                  {entitlement.isPro && !entitlement.cancelAtPeriodEnd && (
                    confirmCancel ? (
                      <div className="bp-cancel-confirm">
                        <div>
                          <p className="bp-cancel-confirm-title">{t('account:billing.actions.cancelConfirmTitle')}</p>
                          <p className="bp-cancel-confirm-desc">
                            {t('account:billing.actions.cancelConfirmDesc')}
                          </p>
                        </div>
                        <div className="bp-cancel-confirm-actions">
                          <button
                            type="button"
                            className="bp-danger-btn"
                            onClick={onCancelSubscription}
                            disabled={canceling}
                            aria-busy={canceling}
                          >
                            {canceling ? t('account:billing.actions.canceling') : t('account:billing.actions.yesCancel')}
                          </button>
                          <button
                            type="button"
                            className="bp-keep-btn"
                            onClick={() => setConfirmCancel(false)}
                          >
                            {t('account:billing.actions.keepPro')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="bp-ghost-btn"
                        onClick={() => setConfirmCancel(true)}
                      >
                        {t('account:billing.actions.cancelSubscription')}
                      </button>
                    )
                  )}
                </div>
              </>
            )}

            {!loading && !entitlement && !error && (
              <p style={{ fontSize: '14px', color: 'var(--ac-text-tertiary)' }}>
                {t('account:billing.noSubscription')}
              </p>
            )}

            {!loading && error && !entitlement && (
              <div className="bp-error" role="alert">
                <ErrorIcon />
                {error}
              </div>
            )}
          </div>
        </section>

        {/* Invoice history */}
        {!loading && invoices.length > 0 && (
          <section className="bp-card" aria-label={t('account:billing.paymentHistory_aria')}>
            <div className="bp-card-header">
              <span className="bp-card-title">{t('account:billing.paymentHistoryTitle')}</span>
            </div>
            <ul className="bp-invoice-list" aria-label={t('account:billing.invoices_aria')}>
              {invoices.map((inv) => (
                <li key={inv.id} className="bp-invoice-item">
                  <span className="bp-invoice-date">
                    {formatDate(inv.createdAt, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="bp-invoice-amount">
                    {formatAmount(inv.amountPaid, inv.currency)}
                  </span>
                  <span className={`bp-invoice-status bp-invoice-status--${inv.status}`}>
                    {inv.status}
                  </span>
                  {isSafeHttpsUrl(inv.hostedInvoiceUrl) ? (
                    <a
                      className="bp-invoice-link"
                      href={inv.hostedInvoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t('account:billing.invoiceView_aria', { date: formatDate(inv.createdAt, { month: 'short', day: 'numeric', year: 'numeric' }) })}
                    >
                      {t('account:billing.invoiceView')} <ExternalIcon />
                    </a>
                  ) : (
                    <span />
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {!loading && invoices.length === 0 && entitlement?.isPro && (
          <section className="bp-card" aria-label={t('account:billing.paymentHistory_aria')}>
            <div className="bp-card-header">
              <span className="bp-card-title">{t('account:billing.paymentHistoryTitle')}</span>
            </div>
            <p className="bp-invoice-empty">{t('account:billing.noInvoices')}</p>
          </section>
        )}

        {/* Danger zone */}
        <section className="bp-card bp-card--danger" aria-label={t('account:billing.dangerZone_aria')}>
          <div className="bp-card-header">
            <span className="bp-card-title bp-card-title--danger">{t('account:billing.dangerZoneTitle')}</span>
          </div>
          <div className="bp-card-body">
            {!showDeleteZone ? (
              <div className="bp-danger-zone-row">
                <div>
                  <p className="bp-danger-zone-title">{t('account:billing.deleteAccountTitle')}</p>
                  <p className="bp-danger-zone-desc">
                    {t('account:billing.deleteAccountDesc')}
                  </p>
                </div>
                <button
                  type="button"
                  className="bp-delete-account-btn"
                  onClick={() => setShowDeleteZone(true)}
                >
                  {t('account:billing.deleteAccountBtn')}
                </button>
              </div>
            ) : (
              <div className="bp-delete-confirm-zone">
                <p className="bp-delete-confirm-warning">
                  <strong>{t('account:billing.deleteConfirm.warning')}</strong>
                </p>
                <ul className="bp-delete-confirm-list">
                  <li>{t('account:billing.deleteConfirm.list.deleteContent')}</li>
                  <li>{t('account:billing.deleteConfirm.list.cancelSubscription')}</li>
                  <li>{t('account:billing.deleteConfirm.list.removeAccount')}</li>
                  <li>{t('account:billing.deleteConfirm.list.signOut')}</li>
                </ul>
                <label className="bp-delete-label" htmlFor="bp-delete-input">
                  <Trans i18nKey="account:billing.deleteConfirm.typeToConfirm" components={{ strong: <strong /> }} />
                </label>
                <input
                  id="bp-delete-input"
                  className="bp-delete-input"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={t('account:billing.deleteConfirm.inputPlaceholder')}
                  autoComplete="off"
                  disabled={deleting}
                />
                {deleteError && (
                  <div className="bp-error" role="alert">
                    <ErrorIcon />
                    {deleteError}
                  </div>
                )}
                <div className="bp-delete-confirm-actions">
                  <button
                    type="button"
                    className="bp-danger-btn"
                    onClick={onDeleteAccount}
                    disabled={deleteConfirmText !== 'DELETE' || deleting}
                    aria-busy={deleting}
                  >
                    {deleting ? t('account:billing.deleteConfirm.deleting') : t('account:billing.deleteConfirm.confirmBtn')}
                  </button>
                  <button
                    type="button"
                    className="bp-keep-btn"
                    onClick={() => { setShowDeleteZone(false); setDeleteConfirmText(''); setDeleteError(null); }}
                    disabled={deleting}
                  >
                    {t('account:billing.deleteConfirm.cancelBtn')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

      </div>
    </AccountShell>
  );
};

export default BillingPage;
